import type { ProfileConfig } from "@worker/config-schema";
import { el } from "../../lib/dom";
import { mountClockWidget } from "./clock";
import { mountLanyardWidget } from "./lanyard";
import { mountDiscordServerWidget } from "./discord-server";
import { mountGithubWidget } from "./github";
import { mountTwitchWidget } from "./twitch";
import { mountSteamWidget } from "./steam";
import { mountLastfmWidget } from "./lastfm";
import { mountAnilistWidget } from "./anilist";
import { mountOsuWidget } from "./osu";
import { mountYoutubeWidget } from "./youtube";

type Widget = ProfileConfig["widgets"][number];
type Size = "sm" | "md" | "lg";

function sizeOf(w: Widget): Size {
  return ("size" in w ? (w.size as Size) : undefined) ?? "md";
}

function mountOne(
  w: Widget,
  inline: ProfileConfig["profile"]["inlineClock"],
): HTMLElement {
  let node: HTMLElement;
  switch (w.type) {
    case "clock":
      node = mountClockWidget(w, inline);
      break;
    case "lanyard":
      node = mountLanyardWidget(w);
      break;
    case "discord-server":
      node = mountDiscordServerWidget(w);
      break;
    case "github":
      node = mountGithubWidget(w);
      break;
    case "twitch":
      node = mountTwitchWidget(w);
      break;
    case "steam":
      node = mountSteamWidget(w);
      break;
    case "lastfm":
      node = mountLastfmWidget(w);
      break;
    case "anilist":
      node = mountAnilistWidget(w);
      break;
    case "osu":
      node = mountOsuWidget(w);
      break;
    case "youtube":
      node = mountYoutubeWidget(w);
      break;
  }
  node.setAttribute("data-size", sizeOf(w));
  return node;
}

/**
 * Widget layout with auto-pairing.
 *
 * Rules (applied in order):
 *   1. Widgets sharing the same explicit `row` number group together.
 *   2. Within the remaining list, consecutive `sm` widgets greedily pair
 *      two-per-row (iOS-style half-squares).
 *   3. `md` and `lg` widgets always take a full row.
 */
export function mountWidgets(
  widgets: Widget[],
  inline: ProfileConfig["profile"]["inlineClock"] = {
    enabled: false,
    timezone: "UTC",
    format: "24",
    showSeconds: true,
  },
): HTMLElement | null {
  if (widgets.length === 0) return null;
  const container = el("div", { class: "widgets", role: "list" });

  // Partition into row groups.
  //
  // Row value semantics:
  //   undefined or 0 → "auto" (no explicit pairing request)
  //   1, 2, 3, …     → explicit group id; widgets with matching id sit together
  //
  // Auto widgets pair greedily by size: two adjacent `sm` widgets sit side
  // by side; `md`/`lg` each take a full row.
  const explicitRow = (w: Widget): number | null => {
    const r = "row" in w ? (w as { row?: number }).row : undefined;
    return r !== undefined && r > 0 ? r : null;
  };

  const groups: Widget[][] = [];
  let i = 0;
  while (i < widgets.length) {
    const cur = widgets[i]!;
    const curRow = explicitRow(cur);

    // Explicit row id — gather adjacent widgets with matching id.
    if (curRow !== null) {
      const group: Widget[] = [cur];
      let j = i + 1;
      while (j < widgets.length && explicitRow(widgets[j]!) === curRow) {
        group.push(widgets[j]!);
        j++;
      }
      groups.push(group);
      i = j;
      continue;
    }

    // Auto: size-driven pair for consecutive sm widgets.
    if (
      sizeOf(cur) === "sm" &&
      i + 1 < widgets.length &&
      sizeOf(widgets[i + 1]!) === "sm" &&
      explicitRow(widgets[i + 1]!) === null
    ) {
      groups.push([cur, widgets[i + 1]!]);
      i += 2;
      continue;
    }

    groups.push([cur]);
    i++;
  }

  for (const group of groups) {
    const rowEl = el("div", {
      class: `widget-row widget-row--${group.length === 2 ? "pair" : "single"}`,
    });
    for (const w of group) rowEl.append(mountOne(w, inline));
    container.append(rowEl);
  }

  return container;
}
